/**
 * Animated dancing octopus logo component
 * Reusable across the app (sidebar, login, landing, etc.)
 */

interface OctopusLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeMap = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

export default function OctopusLogo({ size = 'md', className = '' }: OctopusLogoProps) {
  const sizeClass = sizeMap[size];
  // Use unique gradient IDs based on size to avoid conflicts when multiple logos on same page
  const gradientId = `octopus-${size}`;

  return (
    <div className={`${sizeClass} ${className} relative`}>
      <svg viewBox="0 0 100 100" className="w-full h-full octopus-dance">
        {/* Head */}
        <ellipse cx="50" cy="40" rx="22" ry="20" fill={`url(#${gradientId}-head)`} />

        {/* Eyes */}
        <ellipse cx="42" cy="38" rx="4" ry="5" fill="white" />
        <ellipse cx="58" cy="38" rx="4" ry="5" fill="white" />
        <circle cx="43" cy="39" r="2" fill="#1a1a2e" className="octopus-eye" />
        <circle cx="59" cy="39" r="2" fill="#1a1a2e" className="octopus-eye" />

        {/* Smile */}
        <path d="M44 48 Q50 54 56 48" stroke="#1a1a2e" strokeWidth="2" fill="none" strokeLinecap="round" />

        {/* Tentacles */}
        <path d="M30 58 Q28 72 26 80" stroke={`url(#${gradientId}-tentacle)`} strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-1" />
        <path d="M38 60 Q36 74 34 82" stroke={`url(#${gradientId}-tentacle)`} strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-2" />
        <path d="M50 62 Q50 76 50 85" stroke={`url(#${gradientId}-tentacle)`} strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-3" />
        <path d="M62 60 Q64 74 66 82" stroke={`url(#${gradientId}-tentacle)`} strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-4" />
        <path d="M70 58 Q72 72 74 80" stroke={`url(#${gradientId}-tentacle)`} strokeWidth="5" fill="none" strokeLinecap="round" className="tentacle-5" />

        <defs>
          <linearGradient id={`${gradientId}-head`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e84a8a" />
            <stop offset="100%" stopColor="#d63384" />
          </linearGradient>
          <linearGradient id={`${gradientId}-tentacle`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#d63384" />
            <stop offset="100%" stopColor="#e84a8a" />
          </linearGradient>
        </defs>
      </svg>

      <style>{`
        .octopus-dance {
          animation: octo-dance 1s ease-in-out infinite;
        }

        @keyframes octo-dance {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-3px) rotate(2deg); }
        }

        .octopus-eye {
          animation: octo-blink 3s ease-in-out infinite;
        }

        @keyframes octo-blink {
          0%, 45%, 55%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(0.1); }
        }

        .tentacle-1 { animation: wave-1 0.8s ease-in-out infinite; transform-origin: 30px 58px; }
        .tentacle-2 { animation: wave-2 0.8s ease-in-out infinite 0.1s; transform-origin: 38px 60px; }
        .tentacle-3 { animation: wave-3 0.8s ease-in-out infinite 0.2s; transform-origin: 50px 62px; }
        .tentacle-4 { animation: wave-2 0.8s ease-in-out infinite 0.3s; transform-origin: 62px 60px; }
        .tentacle-5 { animation: wave-1 0.8s ease-in-out infinite 0.4s; transform-origin: 70px 58px; }

        @keyframes wave-1 {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }

        @keyframes wave-2 {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }

        @keyframes wave-3 {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }
      `}</style>
    </div>
  );
}
